import React, { useState, useCallback } from 'react';
import ImageUploader from './components/ImageUploader';
import ProcessingStatus from './components/ProcessingStatus';
import ResultsTable from './components/ResultsTable';
import { extractGuestRecords } from './utils/ocrProcessor';
import './App.css';

function App() {
  const [images, setImages] = useState([]);
  const [results, setResults] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const handleImagesUpload = useCallback((uploadedFiles) => {
    setImages(uploadedFiles);
    setResults([]);
    setCurrentPage(1);
  }, []);

  const processImages = async () => {
    if (images.length === 0) return;

    setIsProcessing(true);
    setProgress(0);
    setStatus('Starting OCR processing...');
    setResults([]);

    const allResults = [];
    const totalPages = images.length;

    for (let i = 0; i < images.length; i++) {
      const image = images[i];
      setCurrentPage(i + 1);
      setStatus(`Processing page ${i + 1} of ${totalPages}: ${image.name}`);

      try {
        const pageResult = await extractGuestRecords(
          image,
          i + 1,
          (progress) => {
            setProgress(((i + progress) / totalPages) * 100);
          }
        );
        
        allResults.push(pageResult);
        setResults([...allResults]);
      } catch (error) {
        console.error(`Error processing ${image.name}:`, error);
        allResults.push({
          page: i + 1,
          entries: [],
          error: error.message
        });
        setResults([...allResults]);
      }
    }

    setIsProcessing(false);
    setStatus('Processing complete!');
    setProgress(100);
  };

  const exportToJSON = () => {
    const data = results.length === 1 ? results[0] : results;
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `chandla_records_${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const exportToCSV = () => {
    const allEntries = results.flatMap(r => r.entries || []);
    if (allEntries.length === 0) return;

    const headers = ['Page', 'Name', 'Place', 'Amount'];
    const rows = allEntries.map(entry => [
      entry.page || '',
      `"${(entry.name || '').replace(/"/g, '""')}"`,
      `"${(entry.place || '').replace(/"/g, '""')}"`,
      entry.amount || ''
    ]);

    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `chandla_records_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const clearAll = () => {
    setImages([]);
    setResults([]);
    setProgress(0);
    setStatus('');
    setCurrentPage(1);
  };

  const totalEntries = results.reduce((sum, r) => sum + (r.entries?.length || 0), 0);

  return (
    <div className="app">
      <header className="header">
        <h1>Chandla Book OCR</h1>
        <p>Extract guest records from scanned Chandla Book pages</p>
        <p className="subtitle">100% Client-side - No data leaves your browser</p>
      </header>

      <main className="main">
        <ImageUploader 
          onImagesUpload={handleImagesUpload}
          images={images}
          disabled={isProcessing}
        />

        {images.length > 0 && (
          <div className="actions">
            <button 
              className="btn btn-primary"
              onClick={processImages}
              disabled={isProcessing}
            >
              {isProcessing ? 'Processing...' : `Process ${images.length} Image(s)`}
            </button>
            <button 
              className="btn btn-secondary"
              onClick={clearAll}
              disabled={isProcessing}
            >
              Clear All
            </button>
          </div>
        )}

        {isProcessing && (
          <ProcessingStatus 
            progress={progress}
            status={status}
            currentPage={currentPage}
            totalPages={images.length}
          />
        )}

        {results.length > 0 && (
          <>
            <div className="export-actions">
              <h3>Export Results ({totalEntries} entries found)</h3>
              <button className="btn btn-success" onClick={exportToJSON}>
                Download JSON
              </button>
              <button className="btn btn-success" onClick={exportToCSV}>
                Download CSV
              </button>
            </div>
            <ResultsTable results={results} />
          </>
        )}
      </main>

      <footer className="footer">
        <p>Chandla Book OCR - Client-side OCR for Gujarati Wedding Guest Records</p>
        <p>Your images never leave your browser. All processing happens locally.</p>
      </footer>
    </div>
  );
}

export default App;